import { User } from 'dashboard/models/user';
import { helpCategories, HelpCategoryName } from 'dashboard/views/DashboardContactUsView/constants';
import Button from 'design-system/components/Button';
import { TextAreaField, TextField } from 'design-system/components/FormField';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FormContactUsV2Data {
  requesterName: string;
  requesterEmail: string;
  subject: string;
  description: string;
  tags?: string[];
  helpCategoryName: string;
}

interface FormContactUsV2Props {
  currentUser: User | null;
  helpCategoryName: HelpCategoryName;
  onSubmit: (data: FormContactUsV2Data) => void;
}

const FormContactUsV2 = ({ currentUser, helpCategoryName, onSubmit }: FormContactUsV2Props) => {
  const helpCategoryMetadata = helpCategories.filter(
    (category) => category.name === helpCategoryName,
  )[0];

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useFormSecurely({
    defaultValues: {
      requesterName: `${currentUser?.attributes?.['name-first'] || ''} ${
        currentUser?.attributes?.['name-last'] || ''
      }`.trim(),
      requesterEmail: currentUser?.attributes?.['email-address'] || '',
      subject: '',
      description: '',
      tags: helpCategoryMetadata.tags || [],
      helpCategoryName: helpCategoryName,
    },
    mode: 'onChange',
  });

  const handleFormSubmit = async (data: FormContactUsV2Data) => {
    try {
      await onSubmit(data);
      reset();
    } catch (error) {
      // Handled in route
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} data-test='form-contact-us__form'>
      <TextField
        label='Your name'
        id='form-contact-us__requester-name'
        data-test='form-contact-us__requester-name'
        hasError={!!errors.requesterName}
        errorText={errors.requesterName?.message}
        disabled={
          !!currentUser?.attributes?.['name-first'] ||
          !!currentUser?.attributes?.['name-last'] ||
          isSubmitting
        }
        {...register('requesterName', {
          required: 'Please enter your name',
        })}
      />

      <TextField
        label='Your email address'
        id='form-contact-us__requester-email'
        data-test='form-contact-us__requester-email'
        hasError={!!errors.requesterEmail}
        errorText={errors.requesterEmail?.message}
        disabled={!!currentUser?.attributes?.['email-address'] || isSubmitting}
        {...register('requesterEmail', {
          required: 'Please enter a valid email address',
          pattern: {
            value: EMAIL_ADDRESS_REGEX,
            message: 'Please enter a valid email address.',
          },
        })}
      />

      <TextField
        label='Brief description of the issue'
        id='form-contact-us__subject'
        data-test='form-contact-us__subject'
        placeholder='Issue with...'
        hasError={!!errors.subject}
        errorText={errors.subject?.message}
        disabled={isSubmitting}
        {...register('subject', {
          required: 'Please enter a brief description of the issue',
        })}
      />

      <TextAreaField
        label='Detailed description of the issue'
        id='form-contact-us__description'
        data-test='form-contact-us__description'
        placeholder={helpCategoryMetadata.placeholderText}
        hasError={!!errors.description}
        errorText={errors.description?.message}
        disabled={isSubmitting}
        {...register('description', {
          required: 'Please enter a detailed description of the issue',
        })}
      />

      <Button
        width='auto'
        type='submit'
        data-test='form-contact-us__submit'
        disabled={isSubmitting}
        isPending={isSubmitting}
      >
        Submit
      </Button>
    </form>
  );
};

export default FormContactUsV2;
